import type { FetchResult } from '@apollo/client/core';
import { filter, from, map, of } from 'rxjs';

import type {
  HotelIngredientResponseByItineraryQL,
  ItineraryResponseQL,
  ItinerarySearchCriteria,
  TripSearchCriteria,
  ItineraryResponse,
  SearchService,
  RecipeResponse,
  TransportIngredientResponseQL,
  ItineraryMainIngredientResponse,
  FerryIngredientAdapter,
  FlightIngredientAdapter,
  HotelIngredientAdapter,
  ItineraryPrices,
  OwnStayIngredientResponseByItineraryQL,
  OwnStayIngredientAdapter,
} from '@libs/trip-planner-interfaces';
import { IngredientType, TransportType } from '@libs/trip-planner-interfaces';
import {
  assertValue,
  exhaustiveGuard,
  isDefined,
  performanceMark,
} from '@libs/utils';

import { getApolloClient } from './infrastructure';
import { SEARCH_ITINERARIES_SUBSCRIPTION } from './queries';
import { logged, zenToRx } from './rx';
import { getRecipeQL, isSameStringContent } from './utils';
import {
  toFerryIngredientResponse,
  toFlightIngredientResponse,
  toHotelIngredientResponse,
  toOwnStayIngredientResponse,
} from './mappers';
import { isAvailableTransportIngredientResponseQL } from './typeGuards';

type ItineraryFetchResult = FetchResult<{
  searchRecipeByItinerary: ItineraryResponseQL;
}>;

type ProvisionalItineraryResponse = {
  date: Date;
  itinerary: ItineraryMainIngredientResponse[];
  prices: ItineraryPrices;
};

let isFirstSearch = true;

export class ItinerarySearchService
  implements SearchService<TripSearchCriteria, ItineraryResponse>
{
  public search(criteria: ItinerarySearchCriteria): RecipeResponse {
    if (
      criteria.userCriteria.ingredients === null ||
      criteria.userCriteria?.ingredients.length === 0
    ) {
      return from([]);
    }

    const recipeCriteria = getRecipeQL(criteria);

    if (isFirstSearch) {
      performanceMark('FirstItineraryRequested');
      isFirstSearch = false;
    }

    const itineraryResponse = zenToRx<ItineraryFetchResult>(
      getApolloClient().subscribe({
        variables: {
          recipe: recipeCriteria,
        },
        query: SEARCH_ITINERARIES_SUBSCRIPTION,
      })
    );

    return itineraryResponse.pipe(
      logged('Itinerary Response'),
      map((fetchResult) => fetchResult.data),
      filter(isDefined),
      map((response): ProvisionalItineraryResponse => {
        const itineraryResponse = response.searchRecipeByItinerary;
        const itinerary = itineraryResponse.result.map((result, index) => {
          const currentIngredient = criteria.userCriteria.ingredients?.[index];
          assertValue(
            currentIngredient,
            'search-service-smart-search > itinerarySearchService.ts > class ItinerarySearchService > currentIngredient'
          );

          switch (result.type) {
            case 'TransportIngredientResponseQL': {
              const transportResult = result as TransportIngredientResponseQL;

              const isAvailable =
                isAvailableTransportIngredientResponseQL(transportResult);

              let isFlight: boolean;
              if (isAvailable) {
                const legs = transportResult.legs;
                assertValue(
                  legs,
                  'search-service-smart-search > itinerarySearchService.ts > class ItinerarySearchService > legs'
                );
                const firstLeg = legs[0];
                assertValue(
                  firstLeg,
                  'search-service-smart-search > itinerarySearchService.ts > class ItinerarySearchService > firstLeg'
                );
                const firstSegment = firstLeg.segments[0];
                assertValue(
                  firstSegment,
                  'search-service-smart-search > itinerarySearchService.ts > class ItinerarySearchService > firstSegment'
                );
                isFlight = isSameStringContent(
                  firstSegment.transportType,
                  TransportType.Flight
                );
              } else {
                isFlight =
                  currentIngredient.ingredientType === IngredientType.Flight;
              }

              if (isFlight) {
                const flightIngredient =
                  currentIngredient as FlightIngredientAdapter;

                return toFlightIngredientResponse(
                  transportResult,
                  flightIngredient
                );
              }

              // INFO: this case must return value. If new transport type will be added refactor to swich statement
              else {
                const ferryIngredient =
                  currentIngredient as FerryIngredientAdapter;

                return toFerryIngredientResponse(
                  transportResult,
                  ferryIngredient
                );
              }
            }

            case 'HotelIngredientResponseQL': {
              const hotelResult =
                result as HotelIngredientResponseByItineraryQL;

              const hotelIngredient =
                currentIngredient as HotelIngredientAdapter;

              return toHotelIngredientResponse(hotelResult, hotelIngredient);
            }

            case 'OwnStayResultQL': {
              const ownStayResult =
                result as OwnStayIngredientResponseByItineraryQL;

              const ownStayIngredient =
                currentIngredient as OwnStayIngredientAdapter;

              return toOwnStayIngredientResponse(
                ownStayResult,
                ownStayIngredient
              );
            }

            case 'TicketIngredientResponseQL':
              throw new Error('Standalone tickets not supported');

            default:
              return exhaustiveGuard(
                result.type,
                'Smart Search ingredient response type not recognized'
              );
          }
        });

        const firstIngredient = itinerary[0];
        assertValue(
          firstIngredient,
          'search-service-smart-search > itinerarySearchService.ts > class ItinerarySearchService > firstIngredient'
        );

        const date = firstIngredient.date;

        return {
          date,
          itinerary,
          prices: itineraryResponse.prices,
        };
      }),
      map<ProvisionalItineraryResponse, ItineraryResponse>(
        (provisionalItineraryResponse) => ({
          ...provisionalItineraryResponse,
          itinerary: provisionalItineraryResponse.itinerary.map((i) => of(i)),
        })
      )
    );
  }
}
